import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiClient from '../../services/apiClient';
import paymentService from '../../services/paymentService';

/** API wraps outcome in { data: { payment_outcome } } on errors; success may use same shape. */
const extractPaymentOutcome = (payload) => {
  if (!payload) return null;
  return payload.data?.payment_outcome ?? payload.payment_outcome ?? null;
};

const resolveDisplayMessage = (payload) => {
  const outcome = extractPaymentOutcome(payload);
  if (outcome?.user_message) return outcome.user_message;
  return payload?.message || null;
};

const moneyWasDeducted = (outcome) => {
  const stripeProof = outcome?.stripe_payment_proof;
  const paypalProof = outcome?.paypal_payment_proof;
  if (stripeProof?.amount_received_cents != null) {
    return stripeProof.amount_received_cents > 0;
  }
  if (paypalProof?.amount_received_cents != null) {
    return paypalProof.amount_received_cents > 0;
  }
  return !!outcome?.payment_captured;
};

const getRefundLabel = (outcome) => {
  const { refund_status, refund_amount_display, refund_id } = outcome;
  switch (refund_status) {
    case 'issued':
      return {
        state: 'done',
        text: `Refund issued${refund_amount_display ? ` (${refund_amount_display})` : ''}. Usually appears in 3–5 business days.`,
        ref: refund_id,
      };
    case 'pending':
      return {
        state: 'pending',
        text: `Refund processing${refund_amount_display ? ` (${refund_amount_display})` : ''}. Usually appears in 3–5 business days.`,
        ref: refund_id,
      };
    case 'already_refunded':
      return {
        state: 'done',
        text: `Already refunded${refund_amount_display ? ` (${refund_amount_display})` : ''}.`,
        ref: refund_id,
      };
    case 'failed':
      return {
        state: 'failed',
        text: 'Automatic refund failed — please contact support with your payment reference.',
        ref: refund_id,
      };
    case 'none':
      return {
        state: 'failed',
        text: 'No refund on record yet — contact support if you were charged.',
        ref: refund_id,
      };
    default:
      return { state: 'na', text: 'Not applicable', ref: null };
  }
};

/** Short lead copy when steps carry the detail (avoids repeating the long API message). */
const getLeadMessage = (outcome, fallback) => {
  if (!outcome) return fallback;
  const rs = outcome.refund_status;
  if (rs === 'issued' || rs === 'already_refunded') {
    return 'Your booking session expired before we could confirm the flight. A full refund has been issued to your payment method.';
  }
  if (rs === 'pending') {
    return 'Your booking session expired before we could confirm the flight. A full refund is being processed.';
  }
  if (!moneyWasDeducted(outcome)) {
    return fallback || 'No payment was taken. You can search for flights and try again.';
  }
  return fallback;
};

/** Heading + icon variant for the error state */
const getErrorPresentation = (outcome) => {
  if (!outcome) {
    return { variant: 'error', title: 'Payment issue' };
  }

  const deducted = moneyWasDeducted(outcome);
  const rs = outcome.refund_status;

  if (!deducted) {
    return { variant: 'info', title: 'No payment taken' };
  }
  if (rs === 'issued' || rs === 'pending' || rs === 'already_refunded') {
    return { variant: 'refunded', title: "Booking couldn't be completed" };
  }
  if (rs === 'failed' || rs === 'none') {
    return { variant: 'warning', title: 'Payment issue — action needed' };
  }
  return { variant: 'error', title: 'Payment issue' };
};

const StatusIcon = ({ variant }) => {
  if (variant === 'refunded') {
    return (
      <div className="text-amber-500 mb-4">
        <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      </div>
    );
  }
  if (variant === 'info') {
    return (
      <div className="text-blue-500 mb-4">
        <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </div>
    );
  }
  if (variant === 'warning') {
    return (
      <div className="text-amber-600 mb-4">
        <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      </div>
    );
  }
  return (
    <div className="text-red-500 mb-4">
      <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    </div>
  );
};

const StepRow = ({ done, pending, failed, label, detail }) => {
  let icon = '○';
  let iconClass = 'text-gray-400';
  if (done) {
    icon = '✓';
    iconClass = 'text-green-600';
  } else if (pending) {
    icon = '…';
    iconClass = 'text-amber-600';
  } else if (failed) {
    icon = '✕';
    iconClass = 'text-red-600';
  }

  return (
    <li className="flex gap-3 text-sm">
      <span className={`font-bold shrink-0 w-4 ${iconClass}`} aria-hidden>
        {icon}
      </span>
      <div>
        <p className="font-semibold text-gray-900 m-0">{label}</p>
        <p className="text-gray-600 m-0 mt-0.5">{detail}</p>
      </div>
    </li>
  );
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** After a slow callback, poll a lightweight verify endpoint until booking exists. */
const pollForConfirmedBooking = async (sessionId, maxAttempts = 20, intervalMs = 2000) => {
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    try {
      const result = await paymentService.verifyStripePayment(sessionId);
      const token = result?.data?.bookingToken || result?.data?.booking_token;
      if (result?.success && token) {
        return token;
      }
      if (result?.data?.status === 'processing') {
        await sleep(intervalMs);
        continue;
      }
      break;
    } catch {
      await sleep(intervalMs);
    }
  }
  return null;
};

const PaymentCallbackPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Processing your payment...');
  const [paymentOutcome, setPaymentOutcome] = useState(null);
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) {
      return;
    }

    const processPayment = async () => {
      hasProcessed.current = true;

      const provider = searchParams.get('provider');
      const callbackStatus = searchParams.get('status');
      const sessionId = searchParams.get('session_id');
      const token = searchParams.get('token');
      const payerId = searchParams.get('PayerID');

      if (callbackStatus === 'cancel') {
        setStatus('cancelled');
        setMessage('Payment was cancelled. No charge was made.');
        sessionStorage.removeItem('pending_booking_token');
        sessionStorage.removeItem('pending_booking_time');
        apiClient.get(`/payments/paypal/cancel?token=${token || ''}`).catch(() => {});
        apiClient.post('/payments/clear-change-flight').catch(() => {});
        return;
      }

      const redirectToBookingConfirmation = (bookingToken, successMessage) => {
        setStatus('success');
        setMessage(successMessage || 'Payment successful! Redirecting to booking confirmation...');
        sessionStorage.removeItem('pending_booking_token');
        sessionStorage.removeItem('pending_booking_time');
        setTimeout(() => {
          navigate(`/booking-confirmation/${bookingToken}`, { replace: true });
        }, 2000);
      };

      try {
        let response;

        const paymentCallbackOptions = { timeout: 90000 };

        if (provider === 'stripe' && sessionId) {
          response = await apiClient.get(
            `/payments/stripe/success?session_id=${sessionId}`,
            {},
            paymentCallbackOptions
          );
        } else if (provider === 'paypal' && token && payerId) {
          response = await apiClient.get(
            `/payments/paypal/success?token=${token}&PayerID=${payerId}`,
            {},
            paymentCallbackOptions
          );
        } else {
          throw new Error('Invalid payment parameters');
        }

        const outcome = extractPaymentOutcome(response);
        if (outcome) setPaymentOutcome(outcome);

        if (response.success && response.data?.bookingToken) {
          redirectToBookingConfirmation(
            response.data.bookingToken,
            resolveDisplayMessage(response) || 'Payment successful! Redirecting to booking confirmation...'
          );
        } else {
          throw new Error(resolveDisplayMessage(response) || response.message || 'Payment processing failed');
        }
      } catch (error) {
        console.error('Payment callback error:', error);

        const isTimeout = error.message === 'Request timeout';
        if (isTimeout && provider === 'stripe' && sessionId) {
          setStatus('processing');
          setMessage('Payment received — confirming your booking. This may take a moment...');

          const recoveredToken = await pollForConfirmedBooking(sessionId);
          if (recoveredToken) {
            redirectToBookingConfirmation(
              recoveredToken,
              'Your booking was confirmed. Redirecting to your confirmation page...'
            );
            return;
          }
        }

        setStatus('error');

        const outcome = extractPaymentOutcome(error.data);
        if (outcome) setPaymentOutcome(outcome);

        apiClient.post('/payments/clear-change-flight').catch(() => {});

        if (error.status === 401) {
          const currentUrl = window.location.pathname + window.location.search;
          sessionStorage.setItem('redirectAfterLogin', currentUrl);
          setMessage(
            'Your login session expired during payment. Please log in again. If you were charged, check your email or contact support.'
          );
          setTimeout(() => {
            navigate('/login', { replace: true });
          }, 3000);
          return;
        }

        const displayMessage = resolveDisplayMessage(error.data) || error.data?.message || error.message;

        if (error.status === 410 || error.status === 500) {
          setMessage(
            displayMessage ||
              'We could not complete your booking. Please check the payment details below or contact support.'
          );
          return;
        }

        if (error.status === 403) {
          setMessage('Payment session does not match your account. Please contact support.');
          setTimeout(() => {
            navigate('/', { replace: true });
          }, 4000);
          return;
        }

        setMessage(displayMessage || 'Payment processing failed');

        if (isTimeout && provider === 'stripe') {
          setMessage(
            'We could not confirm your booking in time. If you were charged, your booking may still have been created — check your email or My Bookings before trying again.'
          );
        }

        setTimeout(() => {
          navigate('/', { replace: true });
        }, 8000);
      }
    };

    processPayment();
  }, [searchParams, navigate]);

  const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard?.writeText(text).catch(() => {});
  };

  const renderStatusSteps = () => {
    if (!paymentOutcome) return null;

    const deducted = moneyWasDeducted(paymentOutcome);
    const proof = paymentOutcome.stripe_payment_proof || paymentOutcome.paypal_payment_proof;
    const amount =
      proof?.amount_received_display || paymentOutcome.amount_charged_display || 'your payment';
    const refund = getRefundLabel(paymentOutcome);

    return (
      <ul className="mt-5 text-left space-y-4 list-none p-0 m-0 border-t border-gray-100 pt-5">
        <StepRow
          done={deducted}
          label="Payment"
          detail={deducted ? `Received — ${amount}` : 'No charge on your card'}
        />
        <StepRow
          done={paymentOutcome.booking_created}
          failed={!paymentOutcome.booking_created && deducted}
          label="Booking"
          detail={paymentOutcome.booking_created ? 'Confirmed' : 'Not created — session expired before checkout finished'}
        />
        {deducted && paymentOutcome.refund_status !== 'not_applicable' && (
          <StepRow
            done={refund.state === 'done'}
            pending={refund.state === 'pending'}
            failed={refund.state === 'failed'}
            label="Refund"
            detail={refund.text}
          />
        )}
      </ul>
    );
  };

  const renderPaymentReference = () => {
    const ref =
      paymentOutcome?.payment_intent_id ||
      paymentOutcome?.stripe_payment_proof?.payment_intent_id ||
      paymentOutcome?.paypal_payment_proof?.capture_id;
    if (!ref) return null;

    return (
      <div className="mt-4 flex items-center justify-center gap-2 text-xs text-gray-500">
        <span>Reference:</span>
        <code className="bg-gray-100 px-2 py-1 rounded break-all max-w-[200px]">{ref}</code>
        <button
          type="button"
          onClick={() => copyToClipboard(ref)}
          className="text-blue-600 underline shrink-0"
        >
          Copy
        </button>
      </div>
    );
  };

  const renderPaymentProof = () => {
    const stripeProof = paymentOutcome?.stripe_payment_proof;
    const paypalProof = paymentOutcome?.paypal_payment_proof;
    const proof = stripeProof || paypalProof;
    if (!proof) return null;

    const isPayPal = proof.source === 'paypal_api';

    return (
      <details className="mt-5 text-left text-sm border border-gray-200 rounded-lg bg-gray-50 group">
        <summary className="cursor-pointer px-4 py-3 font-semibold text-gray-800 list-none flex items-center justify-between">
          <span>Payment verification details</span>
          <span className="text-gray-400 text-xs group-open:rotate-180 transition-transform">▼</span>
        </summary>
        <div className="px-4 pb-4 border-t border-gray-200 bg-white rounded-b-lg">
          <p className="text-xs text-gray-500 mt-3 mb-2">
            Verified {new Date(proof.verified_at).toLocaleString()}
            {isPayPal ? ` · ${proof.paypal_mode} mode` : ` · ${proof.stripe_mode} mode`}
          </p>
          <ul className="space-y-2 text-xs text-gray-700 list-none p-0 m-0">
            {isPayPal ? (
              <>
                {paypalProof.order_id && (
                  <li>
                    <span className="font-semibold">Order: </span>
                    <code className="break-all">{paypalProof.order_id}</code>
                  </li>
                )}
                {paypalProof.capture_id && (
                  <li>
                    <span className="font-semibold">Capture: </span>
                    <code className="break-all">{paypalProof.capture_id}</code>
                  </li>
                )}
              </>
            ) : (
              <>
                {stripeProof.payment_intent_id && (
                  <li>
                    <span className="font-semibold">Payment: </span>
                    <code className="break-all">{stripeProof.payment_intent_id}</code>
                  </li>
                )}
                {stripeProof.charge_id && (
                  <li>
                    <span className="font-semibold">Charge: </span>
                    <code className="break-all">{stripeProof.charge_id}</code>
                  </li>
                )}
              </>
            )}
            {proof.refunds?.map((r) => (
              <li key={r.id}>
                <span className="font-semibold">Refund: </span>
                <code className="break-all">{r.id}</code>
                {r.amount_display ? ` · ${r.amount_display}` : ''} · {r.status}
              </li>
            ))}
          </ul>
          <div className="mt-3 flex flex-wrap gap-3">
            {stripeProof?.receipt_url && (
              <a
                href={stripeProof.receipt_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-semibold text-blue-700 underline"
              >
                View Stripe receipt
              </a>
            )}
            {(stripeProof?.payment_intent_id || paypalProof?.capture_id) && (
              <button
                type="button"
                onClick={() =>
                  copyToClipboard(stripeProof?.payment_intent_id || paypalProof?.capture_id)
                }
                className="text-xs text-gray-600 underline"
              >
                Copy reference ID
              </button>
            )}
          </div>
        </div>
      </details>
    );
  };

  const renderRefundErrors = () => {
    const err =
      paymentOutcome?.paypal_refund_error_message || paymentOutcome?.stripe_refund_error_message;
    if (!err) return null;
    return (
      <p className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 text-left break-all">
        {err}
      </p>
    );
  };

  const errorPresentation = getErrorPresentation(paymentOutcome);
  const leadMessage = getLeadMessage(paymentOutcome, message);

  const cardWidth = status === 'error' ? 'max-w-lg' : 'max-w-md';

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className={`w-full ${cardWidth} bg-white shadow-lg rounded-lg p-6 sm:p-8 max-h-[90vh] overflow-y-auto`}>
        <div className="text-center">
          {status === 'processing' && (
            <>
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-yellow-500 mx-auto mb-4"></div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Processing Payment</h2>
              <p className="text-gray-600">{message}</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="text-green-500 mb-4">
                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Payment Successful!</h2>
              <p className="text-gray-600">{message}</p>
            </>
          )}

          {status === 'cancelled' && (
            <>
              <div className="text-blue-500 mb-4">
                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Payment Cancelled</h2>
              <p className="text-gray-600">{message}</p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  type="button"
                  onClick={() => navigate('/search-flight', { replace: true })}
                  className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold py-2.5 px-6 rounded transition-colors"
                >
                  Search flights
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/', { replace: true })}
                  className="border border-gray-300 hover:bg-gray-50 text-gray-800 font-semibold py-2.5 px-6 rounded transition-colors"
                >
                  Return home
                </button>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <StatusIcon variant={errorPresentation.variant} />
              <h2 className="text-2xl font-bold text-gray-800 mb-2">{errorPresentation.title}</h2>
              <p className="text-gray-600 text-sm leading-relaxed">{leadMessage}</p>
              {renderStatusSteps()}
              {renderRefundErrors()}
              {renderPaymentReference()}
              {renderPaymentProof()}
              <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  type="button"
                  onClick={() => navigate('/search-flight', { replace: true })}
                  className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold py-2.5 px-6 rounded transition-colors"
                >
                  Search flights again
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/', { replace: true })}
                  className="border border-gray-300 hover:bg-gray-50 text-gray-800 font-semibold py-2.5 px-6 rounded transition-colors"
                >
                  Return home
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentCallbackPage;
